import { useState } from 'react';
import { Button } from "@/components/ui/button";
import {
  Users,
  Target,
  MessageSquare,
  Mail,
  Crown,
  X,
  TrendingUp,
  Info
} from "lucide-react";
import { cn } from '@/lib/utils';

type ActionType = 'team' | 'member' | 'feedback_cycle' | 'one_on_one' | 'feedback_email';

interface ProminentUsageBannerProps {
  actionType: ActionType;
  className?: string;
  dismissible?: boolean;
  onDismiss?: () => void;
}

const actionTypeConfig = {
  team: {
    label: 'Teams',
    icon: Users,
    singular: 'team',
    plural: 'teams'
  },
  member: {
    label: 'Team Members',
    icon: Users,
    singular: 'team member',
    plural: 'team members'
  },
  feedback_cycle: {
    label: 'Feedback Cycles',
    icon: Target,
    singular: 'feedback cycle',
    plural: 'feedback cycles'
  },
  one_on_one: {
    label: '1-on-1 Sessions',
    icon: MessageSquare,
    singular: '1-on-1 session',
    plural: '1-on-1 sessions'
  },
  feedback_email: {
    label: 'Feedback Emails',
    icon: Mail,
    singular: 'feedback email',
    plural: 'feedback emails'
  }
};

export function ProminentUsageBanner({
  actionType,
  className = "",
  dismissible = false,
  onDismiss
}: ProminentUsageBannerProps) {
  const [isDismissed, setIsDismissed] = useState(false);

  // Mock usage data for React frontend
  const mockUsage = {
    current: actionType === 'team' ? 3 : 15,
    limit: actionType === 'team' ? 10 : 50,
    percentage: actionType === 'team' ? 30 : 30,
    limitReached: false
  };

  if (mockUsage.percentage < 80 && !mockUsage.limitReached || isDismissed) {
    return null;
  }

  const handleDismiss = () => {
    setIsDismissed(true);
    onDismiss?.();
  };

  const config = actionTypeConfig[actionType];
  const isCritical = mockUsage.limitReached || mockUsage.percentage >= 100;

  // Clean minimal styling
  const bannerStyle = isCritical
    ? {
        bgClass: "border-destructive/15 bg-destructive/5",
        iconClass: "text-destructive",
        iconBgClass: "bg-destructive/10 text-destructive",
        titleClass: "text-destructive font-medium",
        subtitleClass: "text-destructive/80",
        progressClass: "bg-gradient-to-r from-destructive/60 to-destructive/80",
        progressBgClass: "bg-muted/30",
        buttonClass: "bg-destructive/80 hover:bg-destructive/70 text-white border border-destructive/30",
        outlineButtonClass: "border-destructive/40 text-destructive hover:bg-destructive/10"
      }
    : {
        bgClass: "bg-gradient-to-br from-amber-50/80 via-amber-50/60 to-orange-50/80 border-amber-200/50 backdrop-blur-sm",
        iconClass: "text-amber-600",
        iconBgClass: "bg-amber-100/60 border-amber-200/40",
        titleClass: "text-amber-700 font-medium",
        subtitleClass: "text-amber-600/80",
        progressClass: "bg-gradient-to-r from-amber-400 to-amber-500",
        progressBgClass: "bg-amber-100/40",
        buttonClass: "bg-amber-500 hover:bg-amber-600 text-white shadow-sm hover:shadow-md transition-all duration-200",
        outlineButtonClass: "border-amber-200 text-amber-600 hover:bg-amber-50/50 backdrop-blur-sm"
      };

  const remaining = Math.max(0, mockUsage.limit - mockUsage.current);
  const message = isCritical
    ? `You've reached your limit of ${mockUsage.limit} ${config.plural}. Ready to unlock more capacity with an upgrade?`
    : `You're using ${mockUsage.current} of ${mockUsage.limit} ${config.plural} (${remaining} remaining). Consider upgrading for unlimited access.`;

  return (
    <div className={cn(
      "relative p-6 rounded-2xl border shadow-sm hover:shadow-md transition-all duration-300",
      bannerStyle.bgClass,
      className
    )}>
      {/* Dismiss button */}
      {dismissible && (
        <Button
          variant="ghost"
          size="sm"
          onClick={handleDismiss}
          className="absolute top-4 right-4 h-6 w-6 p-0 opacity-60 hover:opacity-100 transition-opacity"
        >
          <X className="h-4 w-4" />
        </Button>
      )}

      <div className="flex items-start gap-5">
        {/* Enhanced Icon */}
        <div className="flex-shrink-0">
          <div className={cn(
            "w-12 h-12 rounded-xl flex items-center justify-center shadow-sm border transition-all duration-200",
            bannerStyle.iconBgClass
          )}>
            {isCritical ? (
              <TrendingUp className={cn("h-6 w-6", bannerStyle.iconClass)} />
            ) : mockUsage.percentage >= 90 ? (
              <TrendingUp className={cn("h-6 w-6", bannerStyle.iconClass)} />
            ) : (
              <Info className={cn("h-6 w-6", bannerStyle.iconClass)} />
            )}
          </div>
        </div>

        {/* Enhanced Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 mb-3">
            <h3 className={cn(
              "text-lg font-semibold leading-tight",
              bannerStyle.titleClass
            )}>
              {config.label} Usage {isCritical ? 'Notice' : 'Update'}
            </h3>
            <span className={cn(
              "text-sm font-medium px-2.5 py-1 bg-card/70 backdrop-blur-sm rounded-full border",
              bannerStyle.subtitleClass,
              isCritical ? "border-red-200/40" : "border-amber-200/40"
            )}>
              {Math.round(mockUsage.percentage)}% Used
            </span>
          </div>

          <p className={cn(
            "text-sm mb-4 leading-relaxed",
            bannerStyle.subtitleClass
          )}>
            {message}
          </p>

          {/* Enhanced Progress Bar */}
          <div className="mb-5">
            <div className="flex items-center justify-between mb-3">
              <span className={cn("text-sm font-medium", bannerStyle.subtitleClass)}>
                {mockUsage.current} of {mockUsage.limit} {config.plural.toLowerCase()}
              </span>
              <span className={cn("text-sm font-semibold tabular-nums", bannerStyle.subtitleClass)}>
                {Math.round(mockUsage.percentage)}%
              </span>
            </div>
            <div className={cn(
              "w-full rounded-full h-2.5 overflow-hidden shadow-inner border",
              bannerStyle.progressBgClass,
              isCritical ? "border-red-200/30" : "border-amber-200/30"
            )}>
              <div
                className={cn(
                  "h-full rounded-full transition-all duration-700 ease-out shadow-sm",
                  bannerStyle.progressClass
                )}
                style={{
                  width: `${Math.min(mockUsage.percentage, 100)}%`
                }}
              />
            </div>
          </div>

          {/* Enhanced Action Buttons */}
          <div className="flex items-center gap-3 flex-wrap">
            <Button
              onClick={() => console.log('Upgrade clicked')}
              className={cn(
                "gap-2 font-medium px-4 py-2 rounded-lg",
                bannerStyle.buttonClass
              )}
            >
              <Crown className="h-4 w-4" />
              Upgrade Plan
            </Button>

            <Button
              variant="outline"
              onClick={() => console.log('Contact sales clicked')}
              className={cn(
                "gap-2 bg-card/80 font-medium px-4 py-2 rounded-lg border shadow-sm hover:shadow-md transition-all duration-200",
                bannerStyle.outlineButtonClass
              )}
            >
              <MessageSquare className="h-4 w-4" />
              Contact Sales
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
